import React, { useState } from "react";
import { toast } from "sonner";
import { User, Mail, Globe, Hash, Link2, BadgeCheck, ShieldCheck, Calendar, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { updateProfile } from "../services/auth";
import { verifyTraderId } from "../services/trader";
import { formatApiErrorDetail } from "../lib/apiClient";
import Modal from "../components/common/Modal";
import { Badge } from "../components/common/Card";
import Reveal from "../components/common/Reveal";

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const [showId, setShowId] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", country: user?.country || "" });
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  const maskedId = user.trader_id ? `••••••${user.trader_id.slice(-2)}` : "—";
  const status = user.verification_status || "pending";

  const saveProfile = async () => {
    setBusy(true);
    try { await updateProfile(form); await refreshUser(); setEditOpen(false); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitVerify = async () => {
    setBusy(true);
    try { const r = await verifyTraderId(); await refreshUser(); setVerifyOpen(false); toast.success(r.message); }
    catch { toast.error("Could not submit verification"); }
    finally { setBusy(false); }
  };

  const rows = [
    { icon: User, label: "Name", value: user.name },
    { icon: Mail, label: "Email", value: user.email },
    { icon: Globe, label: "Country", value: user.country || "—" },
    { icon: Hash, label: "Trader ID", value: showId ? user.trader_id : maskedId, toggle: true },
    { icon: Calendar, label: "Registered", value: user.created_at ? new Date(user.created_at).toLocaleDateString() : "—" },
  ];

  return (
    <div className="mx-auto max-w-3xl">
      <Reveal>
        <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500"><User size={14} /> Account</div>
        <h1 className="font-head text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">Profile</h1>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="surface mt-6 rounded-2xl p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/8 pb-6">
            <div className="flex items-center gap-4">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-[#f3f4f6] to-[#aeb3b9] font-head text-lg font-black text-black">
                {user.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
              </span>
              <div>
                <div className="font-head text-lg font-bold text-white">{user.name}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">{user.email}</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge tone={status === "verified" ? "verified" : status === "rejected" ? "rejected" : "pending"}>
                <BadgeCheck size={12} /> {status}
              </Badge>
              <Badge tone="demo">{user.account_status || "active"}</Badge>
            </div>
          </div>

          <div className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
            {rows.map((r) => (
              <div key={r.label} className="flex items-start gap-3">
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/10 text-neutral-500"><r.icon size={14} /></span>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{r.label}</div>
                  <div className="flex items-center gap-2 font-mono text-sm text-white">
                    <span className="truncate">{r.value}</span>
                    {r.toggle && (
                      <button onClick={() => setShowId((s) => !s)} className="text-neutral-500 hover:text-white" data-testid="profile-toggle-id">
                        {showId ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => { setForm({ name: user.name, country: user.country || "" }); setEditOpen(true); }} className="btn-ghost rounded-xl px-5 py-3 font-mono text-[11px] uppercase tracking-widest" data-testid="profile-edit-btn">Edit Profile</button>
            {status !== "verified" && (
              <button onClick={() => setVerifyOpen(true)} className="btn-silver sweep flex items-center gap-2 rounded-xl px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="profile-verify-btn">
                <ShieldCheck size={14} /> Verify Trader ID
              </button>
            )}
            <button onClick={logout} className="rounded-xl border border-rose-400/30 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-rose-300/90 transition-colors hover:bg-rose-400/5" data-testid="profile-logout-btn">Logout</button>
          </div>
        </div>
      </Reveal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit profile" testid="profile-edit-modal"
        footer={<>
          <button onClick={() => setEditOpen(false)} className="btn-ghost rounded-xl px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest">Cancel</button>
          <button onClick={saveProfile} disabled={busy} className="btn-silver flex items-center gap-2 rounded-xl px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="profile-save-btn">{busy ? <Loader2 size={13} className="animate-spin" /> : "Save"}</button>
        </>}>
        <label className="mb-4 block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-neutral-500">Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-[#d4d8dc]/60" />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-neutral-500">Country</span>
          <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="profile-country-input" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-sm text-white outline-none focus:border-[#d4d8dc]/60" />
        </label>
      </Modal>

      <Modal open={verifyOpen} onClose={() => setVerifyOpen(false)} title="Verify Trader ID" subtitle={`Trader ID ${maskedId}`} testid="profile-verify-modal"
        footer={<>
          <button onClick={() => setVerifyOpen(false)} className="btn-ghost rounded-xl px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest">Cancel</button>
          <button onClick={submitVerify} disabled={busy} className="btn-silver flex items-center gap-2 rounded-xl px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="profile-verify-submit">{busy ? <Loader2 size={13} className="animate-spin" /> : "Submit"}</button>
        </>}>
        <p className="text-sm leading-relaxed text-neutral-400">
          Submitting will queue your Trader ID for verification with our team.
          In <span className="text-white">DEMO MODE</span> the status stays <span className="text-amber-300/90">Pending</span> —
          the backend will confirm it once the verification API is connected.
        </p>
      </Modal>
    </div>
  );
}
